(ns arthur.subs.render
  "Stage 6. The subscription graph IS the staged dataflow — each stage is a
  layer-3 sub over the previous one plus the parameters for its stage only,
  which is what makes changing one knob recompute one thing.

  `::resolver` is the shape that matters. It does not yield geometry; it yields a
  CLOSURE that produces geometry at a frame. So a scene edit costs one
  recomputation here and a frame costs a lookup and a blit — and, crucially, the
  playhead is not an input, so moving it cannot invalidate this."
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.gesture :as gesture]
            [arthur.domain.nest :as nest]
            [arthur.domain.node :as node]
            [arthur.domain.palette :as pal]
            [arthur.domain.symbol :as symbol]
            [arthur.footage.store :as footage]
            [arthur.subs.playback :as playback]
            [re-frame.core :as rf]))

(rf/reg-sub ::clip-id (fn [db _] (:clip/current db)))
(rf/reg-sub ::paint-revision (fn [db _] (:paint/revision db)))

(rf/reg-sub ::open (fn [db _] (get-in db [:ui :open])))
(rf/reg-sub ::sliding (fn [db _] (get-in db [:ui :sliding])))
(rf/reg-sub ::gesture (fn [db _] (get-in db [:ui :gesture])))
(rf/reg-sub ::solo (fn [db _] (get-in db [:ui :solo (get-in db [:ui :open])])))
(rf/reg-sub ::tracing (fn [db _] (get-in db [:ui :tracing])))
;; The faces with smart frame picking on, as a PREVIEW switch. The setting
;; docs/frame-selection.md specifies is the document's and is not built; this is
;; the editor's own, so it moves what the stage shows and not what exports.
;; Per FACE, because the closure cuts it reads are stored on the face's nodes.
(rf/reg-sub ::smart (fn [db _] (set (get-in db [:ui :smart]))))

(rf/reg-sub
 ::clip
 :<- [::clip-id]
 :<- [::paint-revision]
 :<- [::sliding]
 :<- [::gesture]
 :<- [::open]
 (fn [[id _ sliding gesture open] _]
   ;; With a timeline bar being slid, the document as it will be when the drag
   ;; lets go, so the stage and the rows follow the pointer. Nothing is written
   ;; until then: one drag is one undo step and one write to collaborators.
   (let [{c :clip st :store} (footage/entry id)]
     (or (when sliding
           (:clip (nest/drag c (or (:owner sliding) open) sliding)))
         (when-let [{:keys [sid id frame values edits]} gesture]
           ;; A held stage control owns the touched parameters completely. Make
           ;; them temporary static channels for the preview, so their existing
           ;; automation cannot pull against the pointer while the performance
           ;; recorder samples the live value in the background. Pointer-up
           ;; removes this preview and commits the buffered take as real keys.
           (when c
             (if (seq edits)
               (reduce (fn [out {:keys [sid id frame values]}]
                         (gesture/apply-values out sid id frame values false st)) c edits)
               (gesture/apply-values c sid id frame values false st))))
         c))))

(rf/reg-sub
 ::sounds
 :<- [::clip-id]
 :<- [::paint-revision]
 :<- [::open]
 (fn [[id _ sid] _]
   ;; What the open symbol plays, off the document as saved rather than `::clip`,
   ;; so a bar being slid does not re-mix on every frame of the drag.
   ;;
   ;; THE DOCUMENT IS PART OF THE IDENTITY, not just the symbol. `ui/shell`
   ;; re-mixes when this changes under the SAME symbol, and two documents both
   ;; opening on `:main` would otherwise look like one symbol whose tracks had
   ;; been edited — so opening a project re-mixed it a second time, on top of
   ;; the mix the open itself had already asked for.
   (let [c (:clip (footage/entry id))]
     [id sid (when (clip/symbol c sid) (nest/audio-tracks c sid))])))

(rf/reg-sub
 ::symbol
 :<- [::clip]
 :<- [::open]
 (fn [[clip sid] _] (some-> clip (clip/symbol sid))))

(rf/reg-sub
 ::frames
 :<- [::clip]
 :<- [::open]
 ;; THE TRANSPORT'S LENGTH: what the clock counts, what the readout reads and
 ;; what export writes. Nil for a symbol that is not there; `::open-frames` is
 ;; the same length read as 0, for drawing a ruler.
 (fn [[document sid] _] (clip/frames document sid)))

(rf/reg-sub
 ::open-frames
 :<- [::clip]
 :<- [::open]
 ;; How many frames the open symbol HAS, in its own frames: the ruler's length,
 ;; because the ruler is the symbol's own frame space and every span drawn
 ;; against it is in that space.
 (fn [[document sid] _] (or (clip/frames document sid) 0)))

(rf/reg-sub
 ::open-frame
 :<- [::clip]
 :<- [::open]
 :<- [::playback/frame]
 ;; Where the playhead is in the open symbol. Every symbol counts in the
 ;; document's one rate, so this is the playhead itself.
 (fn [[_ _ f] _] f))

(rf/reg-sub
 ::exposure
 :<- [::symbol]
 (fn [sym _]
   ;; NOT a cel. This is `:time :expose` — how many frames each step of a
   ;; subtree lasts, which is what shooting on twos means — and it lives on the
   ;; symbol's root node and is INHERITED, so reading it there is reading it
   ;; everywhere. The transport shows it so that `exposure 2` is visibly doing
   ;; something rather than only inside the document.
   (or (get-in sym [:nodes :root :time :expose]) 1)))

(rf/reg-sub
 ::palette
 :<- [::clip]
 (fn [clip _] (when clip (pal/compile clip))))

(rf/reg-sub
 ::frame-palette
 ;; The palette the open tab's stage is in at the frame last painted: an id,
 ;; or `{:from :to :t}` mid-blend. Each tab keeps its own.
 (fn [db _] (get-in db [:ui :frame-palette (get-in db [:ui :open])])))

(rf/reg-sub
 ::palette-solo?
 ;; Show only what is drawn in the chosen colour, and let only it be clicked.
 (fn [db _] (get-in db [:ui :palette-solo?])))

(rf/reg-sub
 ::palette-preview
 ;; A palette under the pointer in the chooser: `{:bank id :hexes […]}`, the
 ;; bank the stage would draw it into. Viewing only; nothing is edited.
 (fn [db _] (get-in db [:ui :palette-preview])))

(rf/reg-sub
 ::store
 :<- [::clip-id]
 :<- [::paint-revision]
 (fn [[id _] _]
   ;; Tier 2, behind a handle, and never in app-db itself — what is in the db is
   ;; the id of the clip whose blocks these are.
   ;;
   ;; ON THE REVISION AS WELL AS THE ID, like `::clip`. An edit can bring blocks
   ;; in with it — a video made into a symbol does — without the id changing, and
   ;; a store read only when the id changed is the old one: the document names the
   ;; new blocks, the resolver cannot find them, and the frame throws.
   (:store (footage/entry id))))

(defn- placed?
  "Does row `path` from symbol `sid` still name an instance, all the way down?"
  [clip sid path]
  ;; Only a cel names a symbol. A lane is a group, so a row path that
  ;; ends at the lane rather than at one of its cels names no placement — which
  ;; is the structural answer, and does not move as the lane cuts.
  (reduce (fn [sid id]
            (or (node/source (get-in clip [:symbols sid :nodes id])) (reduced nil)))
          sid path))

(rf/reg-sub
 ::resolver
 :<- [::clip]
 :<- [::open]
 :<- [::store]
 :<- [::palette]
 :<- [::smart]
 (fn [[document sid store palette smart] _]
   (when (and document (clip/symbol document sid))
     ;; The only resolver that asks for tracing layers: the stage shows them and
     ;; nothing else may. See `clip/trace-op`.
     (clip/resolver document sid store palette {:snap smart :tracing? true}))))

(rf/reg-sub
 ::shown
 :<- [::resolver]
 :<- [::clip]
 :<- [::open]
 :<- [::solo]
 (fn [[resolve document sid solo] _]
   ;; What the stage draws: `::resolver`, less whatever is not under a soloed
   ;; row. Its own layer, so soloing does not rebuild the resolver.
   ;;
   ;; A soloed row that has since been deleted or moved would otherwise leave
   ;; the stage blank, with no row left to un-solo it from.
   (let [solo (filter #(placed? document sid %) solo)]
     (if (or (nil? resolve) (empty? solo))
       resolve
       ;; An op inside an instance is named by the path its row has; one at the
       ;; top by its bare id. Still the resolver underneath, so where a node
       ;; went on the frame is still asked of it.
       (reify
         IFn
         (-invoke [_ f]
           (filterv (fn [{n :node}]
                      (let [p (if (vector? n) n [n])]
                        (some #(= % (take (count %) p)) solo)))
                    (resolve f)))
         symbol/IResolver
         (world-of [_ path] (symbol/world-of resolve path))
         (frame-of [_ path] (symbol/frame-of resolve path))
         (pre-frame-of [_ path] (symbol/pre-frame-of resolve path))
         clip/IActivePalette
         (active-palette [_] (clip/active-palette resolve)))))))
